import {isFeatureEnabled} from 'feature-flags';
import {Button, Card, Separator} from 'platform/components';
import {Box, Center, getSize, Hide, HStack, Show, Text, VStack} from 'platform/foundation';

import {useRef} from 'react';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useIsSourcingCardViewEnforced} from '../../../../hooks/useIsSourcingCardViewEnforced';
import {useSourcingPageHeaderHeight} from '../../../../hooks/useSourcingPageHeaderHeight';
import {useSourcingType} from '../../../../hooks/useSourcingType';
import {SourcingFilterPresetsControl} from '../../hooks/useSourcingFilterPresets';
import {getApplicableFilters} from '../../utils/getApplicableFilters';
import {hasSomeFilters} from '../../utils/hasSomeFilters';
import {isSuspiciousReasonFilterEnabled} from '../../utils/isSuspiciousReasonFilterEnabled';
import {useOfferButtonLabel} from '../FilterEditor/hooks/useOfferButtonLabel';
import {FilterFieldName} from '../FilterEditor/utils/getFilterSections';
import {SavedFiltersMobileBar} from '../FilterPresetButtons/components/SavedFiltersMobileBar';
import {FilterPresetButtons} from '../FilterPresetButtons/FilterPresetButtons';
import {useFilters} from '../FiltersContext/FiltersContext';
import {FilterTags} from '../FilterTags/FilterTags';
import {FilterFeatures} from './components/FilterFeatures/FilterFeatures';
import {FilterMakesModels} from './components/FilterMakesModels/FilterMakesModels';
import {FiltersNavigation} from './components/FilterNavigation/components/FiltersNavigation';
import {FilterSearch} from './components/FilterSearch/FilterSearch';
import {FilterSeller} from './components/FilterSeller/FilterSeller';
import {FilterSpecification} from './components/FilterSpecification/FilterSpecification';
import {FilterStatistics} from './components/FilterStatistics/FilterStatistics';
import {FilterBlockEnum} from './constants/FilterBlockEnum';

export interface FiltersProps extends TestIdProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  count: number | undefined;
  isLoading: boolean;
  isDisabled: boolean;
  hasError: boolean;
  onRetry: () => void;
  onEdit: (field: FilterFieldName) => void;
  onClose: () => void;
}

export function Filters(props: FiltersProps) {
  const tilesWrapperRef = useRef<HTMLDivElement>(null);
  const {filters} = useFilters();
  const sourcingType = useSourcingType();
  const offerButtonLabel = useOfferButtonLabel(props.count, props.isLoading);
  const pageHeaderHeight = useSourcingPageHeaderHeight();
  const isCardView = useIsSourcingCardViewEnforced();

  return (
    <VStack height={`calc(100dvh - ${getSize(pageHeaderHeight)})`}>
      <Box backgroundColor="palettes.white.10.100">
        <Box paddingHorizontal={4} paddingVertical={3}>
          <HStack
            spacing={[2, 2, 4]}
            wrap={isCardView ? true : 'nowrap'}
            justify={isCardView ? 'space-between' : undefined}
          >
            <Hide onMobile onTablet>
              <Center height={8}>
                <Text size="large" alternative>
                  {i18n.t('page.filters.labels.filters')}
                </Text>
              </Center>
              <Show when={!props.filterPresetsControl.isEmpty}>
                <Separator orientation="vertical" spacing={0} />
              </Show>
              <FilterPresetButtons
                filterPresetsControl={props.filterPresetsControl}
                isSortingEnabled
                isCollapsible
              />
            </Hide>
            <Box flexShrink={0}>
              <Button
                title={offerButtonLabel}
                isLoading={props.isLoading}
                isDisabled={props.isDisabled}
                onClick={props.onClose}
                data-testid={suffixTestId('showOffersWithCount', props)}
              />
            </Box>
            <Show onMobile onTablet>
              <SavedFiltersMobileBar
                filterPresetsControl={props.filterPresetsControl}
                isSortingEnabled
              />
            </Show>
          </HStack>
        </Box>
        <Show when={props.hasError}>
          <Button
            variant="dangerLink"
            title={i18n.t('general.actions.retry')}
            onClick={props.onRetry}
          />
        </Show>
        <Separator spacing={0} />
        <Show
          when={
            hasSomeFilters(
              getApplicableFilters(filters, sourcingType, {
                isVanFiltersEnabled: isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS),
                isOriginCountryFilterEnabled: isFeatureEnabled(
                  featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER
                ),
                isSuspiciousVehiclesEnabled: isFeatureEnabled(
                  featureFlags.SOURCING_SUSPICIOUS_VEHICLES
                ),
                isSuspiciousReasonFilterEnabled: isSuspiciousReasonFilterEnabled(),
              })
            ) || props.filterPresetsControl.isOutdated
          }
        >
          <Box padding={4}>
            <FilterTags
              filterPresetsControl={props.filterPresetsControl}
              onEdit={props.onEdit}
              showSaveButton
              data-testid={suffixTestId('filterTags', props)}
            />
          </Box>
          <Separator spacing={0} />
        </Show>
      </Box>
      <Box flex={1} overflow="hidden">
        <HStack height="100%">
          <Hide onMobile onTablet>
            <Box padding={4} paddingRight={0} width={56} flexShrink={0}>
              <VStack spacing={3}>
                <FilterSearch onEdit={props.onEdit} tilesWrapperRef={tilesWrapperRef} />
                <FiltersNavigation
                  tilesWrapperRef={tilesWrapperRef}
                  data-testid={suffixTestId('filtersNavigation', props)}
                />
              </VStack>
            </Box>
          </Hide>
          <Box id="tilesWrapper" flex={1} padding={4} overflowX="hidden" ref={tilesWrapperRef}>
            <VStack spacing={4}>
              <Show onMobile onTablet>
                <FilterSearch onEdit={props.onEdit} tilesWrapperRef={tilesWrapperRef} />
              </Show>
              <Box id={FilterBlockEnum.seller}>
                <Card title={i18n.t('page.filters.labels.seller')}>
                  <FilterSeller data-testid={suffixTestId('filterSeller', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.statistics}>
                <Card title={i18n.t('page.filters.labels.statistics')}>
                  <FilterStatistics data-testid={suffixTestId('filterStatistics', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.makesAndModels}>
                <Card title={i18n.t('page.filters.labels.makeAndModels')}>
                  <FilterMakesModels data-testid={suffixTestId('filterMakesModels', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.specification}>
                <Card title={i18n.t('page.filters.labels.specification')}>
                  <FilterSpecification data-testid={suffixTestId('filterSpecification', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.features}>
                <Card title={i18n.t('general.labels.allFeatures')}>
                  <FilterFeatures data-testid={suffixTestId('filterFeatures', props)} />
                </Card>
              </Box>
            </VStack>
          </Box>
        </HStack>
      </Box>
    </VStack>
  );
}
